// Days and times on the organisation's calendar (Asia/Kolkata for Tatra), whatever the device's own
// time zone: "today", a visit at 11:00 and the day a slot belongs to mean the same for everyone in
// the organisation (D-070). Intl knows the zones; no dependency.
import { useEffect, useState } from 'react';
import { useAuth } from './auth.js';

export const DEFAULT_TIME_ZONE = 'Asia/Kolkata';

export function useTimeZone(): string {
  const { me } = useAuth();
  return me?.organisation.timezone ?? DEFAULT_TIME_ZONE;
}

const cache = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${tz}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-IN', { timeZone: tz, ...opts });
    cache.set(key, f);
  }
  return f;
}

/** 11:00 */
export const timeOfDay = (iso: string | Date, tz: string): string =>
  fmt(tz, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));

/** Sat, 3 Oct */
export const shortDay = (iso: string | Date, tz: string): string =>
  fmt(tz, { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(iso));

/** Saturday 3 October 2026 */
export const longDay = (iso: string | Date, tz: string): string =>
  fmt(tz, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso));

/** 3 Oct 2026, 11:00 */
export const dateTime = (iso: string | Date, tz: string): string =>
  fmt(tz, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));

/** YYYY-MM-DD of the local date of an instant. */
export function dayKey(at: string | Date, tz: string): string {
  const parts = Object.fromEntries(
    fmt(tz, { year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date(at))
      .map((p) => [p.type, p.value]),
  );
  return `${parts['year']}-${parts['month']}-${parts['day']}`;
}

function offsetMs(at: Date, tz: string): number {
  const p = Object.fromEntries(
    fmt(tz, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(at)
      .map((x) => [x.type, x.value]),
  );
  const asUtc = Date.UTC(
    Number(p['year']),
    Number(p['month']) - 1,
    Number(p['day']),
    Number(p['hour']) % 24,
    Number(p['minute']),
    Number(p['second']),
  );
  return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/** The instant of a wall-clock time on a local date (`YYYY-MM-DD`, minutes after midnight). */
export function zonedInstant(day: string, minuteOfDay: number, tz: string): Date {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d, Math.floor(minuteOfDay / 60), minuteOfDay % 60);
  const first = offsetMs(new Date(guess), tz);
  let t = guess - first;
  const second = offsetMs(new Date(t), tz);
  if (second !== first) t = guess - second;
  return new Date(t);
}

/** `YYYY-MM-DD` plus n days (calendar arithmetic, no time zone involved). */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Monday of the week containing `day`. */
export function weekStart(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const weekday = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
  return addDays(day, -weekday);
}

/** "11:00" → 660. */
export function parseClock(hhmm: string): number | null {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(hhmm.trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** A `datetime-local` value in the organisation's zone, and back. */
export function toLocalInput(iso: string | Date, tz: string): string {
  return `${dayKey(iso, tz)}T${timeOfDay(iso, tz)}`;
}
export function fromLocalInput(value: string, tz: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value);
  if (!m) return null;
  const minutes = parseClock(m[2]!);
  return minutes === null ? null : zonedInstant(m[1]!, minutes, tz).toISOString();
}

/** "2 d 4 h", "35 min": how long something has been going on. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const m = Math.floor(seconds / 60);
  if (m < 1) return 'under a minute';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h${m % 60 && h < 10 ? ` ${m % 60} min` : ''}`;
  const d = Math.floor(h / 24);
  return `${d} d${h % 24 && d < 7 ? ` ${h % 24} h` : ''}`;
}

/** `value`, once it has stopped changing for `ms` (search boxes: one request per pause, not per key). */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}
